import React, { useState, useEffect } from 'react';
import API from '../../api/axiosConfig';

export default function CalendarGrid({ userRole, filters, selectedClasses, onEventClick, currentDate, currentView, miniCalendarDate }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        API.get('/api/calendar/events/').then(res => {
            setEvents(res.data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    const getLocalDateStr = (iso) => iso ? new Date(iso).toISOString().split('T')[0] : '';
    const formatTime = (iso) => iso ? new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '';

    const filteredEvents = events.filter(event => {
        const isPersonal = event.event_type === 'personal';
        if (isPersonal && !filters.personal) return false;
        if (!isPersonal && !filters.group) return false;
        if (!isPersonal && event.event_type && filters[event.event_type] === false) return false;
        if (!isPersonal && !selectedClasses.includes(event.target_group_name || (event.class_level ? `${event.class_level} класс` : 'Не указана'))) return false;
        return true;
    });

    const getEventColors = (event) => {
        if (event.event_type === 'personal') return 'bg-blue-500 border-blue-600 text-white';
        if (event.event_type === 'test') return 'bg-purple-500 border-purple-600 text-white';
        if (event.event_type === 'deadline') return 'bg-orange-500 border-orange-600 text-white';
        return 'bg-emerald-500 border-emerald-600 text-white';
    };

    const getEventPosition = (event) => {
        if (!event.start_time) return { top: '0px', height: '60px' };
        const start = new Date(event.start_time);
        const startMinutes = start.getHours() * 60 + start.getMinutes();
        let duration = 60;
        if (event.end_time) duration = (new Date(event.end_time) - start) / 60000;
        if (duration < 30) duration = 30; 
        return { top: `${startMinutes}px`, height: `${duration}px` };
    };

    const hours = Array.from({ length: 24 }).map((_, i) => i === 0 ? '' : `${String(i).padStart(2, '0')}:00`);

    // --- РЕНДЕР ДНЯ ---
    const renderDayView = () => {
        const todayStr = getLocalDateStr(currentDate);
        const dayEvents = filteredEvents.filter(e => getLocalDateStr(e.start_time) === todayStr);

        return (
            <div className="flex flex-col h-full w-full bg-white relative">
                <div className="flex border-b border-gray-200 shrink-0 bg-white z-10 sticky top-0">
                    <div className="w-20 border-r border-gray-100 shrink-0"></div>
                    <div className="flex-1 text-center py-3 text-lg font-medium text-gray-800">
                        {currentDate.toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto custom-scrollbar relative">
                    <div className="flex relative" style={{ height: '1440px' }}>
                        <div className="w-20 shrink-0 border-r border-gray-100 relative bg-white z-10">
                            {hours.map((h, i) => <div key={i} className="absolute w-full text-right pr-2 text-sm text-gray-500 font-medium" style={{ top: `${i * 60 - 10}px` }}>{h}</div>)}
                        </div>
                        <div className="flex-1 relative bg-white">
                            {Array.from({ length: 24 }).map((_, i) => <div key={i} className="absolute w-full border-b border-gray-100 pointer-events-none" style={{ top: `${i * 60}px`, height: '60px' }}></div>)}
                            {dayEvents.map(e => (
                                <div key={e.id} onClick={() => onEventClick(e)} className={`absolute left-1 right-2 p-2 rounded border shadow-sm cursor-pointer hover:opacity-90 transition-opacity overflow-hidden ${getEventColors(e)}`} style={getEventPosition(e)}>
                                    <div className="text-sm font-bold leading-tight">{e.title}</div>
                                    <div className="text-xs opacity-90 mt-0.5">{formatTime(e.start_time)} • {e.target_group_name || 'Личное'}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // --- РЕНДЕР НЕДЕЛИ ---
    const renderWeekView = () => {
        const startOfWeek = new Date(currentDate);
        const day = startOfWeek.getDay();
        startOfWeek.setDate(startOfWeek.getDate() - day + (day === 0 ? -6 : 1));

        const weekDays = Array.from({length: 7}).map((_, i) => {
            const d = new Date(startOfWeek);
            d.setDate(d.getDate() + i);
            return d;
        });

        return (
            <div className="flex flex-col h-full w-full bg-white">
                <div className="flex border-b border-gray-200 shrink-0 bg-white z-10 sticky top-0">
                    <div className="w-16 shrink-0 border-r border-gray-100"></div>
                    {weekDays.map(d => {
                        const isToday = getLocalDateStr(d) === getLocalDateStr(new Date());
                        return (
                            <div key={d} className="flex-1 text-center py-2 border-r border-gray-100 last:border-0">
                                <div className="text-sm uppercase text-gray-500 font-bold">{d.toLocaleDateString('ru-RU', { weekday: 'short' })}</div>
                                <div className={`text-xl md:text-2xl font-normal mx-auto w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full mt-1 ${isToday ? 'bg-blue-600 text-white' : 'text-gray-800'}`}>{d.getDate()}</div>
                            </div>
                        );
                    })}
                </div>
                <div className="flex-1 overflow-y-auto custom-scrollbar relative">
                    <div className="flex relative" style={{ height: '1440px' }}>
                        <div className="w-16 shrink-0 border-r border-gray-100 relative bg-white z-10">
                            {hours.map((h, i) => <div key={i} className="absolute w-full text-right pr-2 text-sm text-gray-500" style={{ top: `${i * 60 - 10}px` }}>{h}</div>)}
                        </div>
                        {weekDays.map((d, index) => {
                            const dateStr = getLocalDateStr(d);
                            const dayEvents = filteredEvents.filter(e => getLocalDateStr(e.start_time) === dateStr);
                            return (
                                <div key={index} className="flex-1 relative border-r border-gray-100 last:border-0 bg-white">
                                    {Array.from({ length: 24 }).map((_, i) => <div key={i} className="absolute w-full border-b border-gray-100 pointer-events-none" style={{ top: `${i * 60}px`, height: '60px' }}></div>)}
                                    {dayEvents.map(e => (
                                        <div key={e.id} onClick={() => onEventClick(e)} className={`absolute left-0.5 right-0.5 p-1.5 rounded cursor-pointer overflow-hidden text-xs shadow-sm hover:opacity-90 leading-tight ${getEventColors(e)}`} style={getEventPosition(e)} title={`${e.title} (${formatTime(e.start_time)})`}>
                                            <span className="font-bold">{e.title}</span>
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    // --- РЕНДЕР МЕСЯЦА (НА ВЕСЬ ЭКРАН) ---
    const renderMonthView = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const firstDay = new Date(year, month, 1).getDay();
        const startDay = firstDay === 0 ? 6 : firstDay - 1; 

        const cells = Array(startDay).fill(null).map((_, i) => ({ empty: true, key: `e-${i}` }));
        const todayStr = getLocalDateStr(new Date());

        for (let i = 1; i <= daysInMonth; i++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
            cells.push({ day: i, dateStr, isToday: dateStr === todayStr, events: filteredEvents.filter(e => getLocalDateStr(e.start_time) === dateStr), key: dateStr });
        }
        while (cells.length % 7 !== 0 || cells.length < 42) cells.push({ empty: true, key: `e-end-${cells.length}` });

        return (
            <div className="flex flex-col w-full h-full bg-white border-l border-gray-200 overflow-hidden">
                {/* Шапка дней недели (без padding сбоку, на всю ширину) */}
                <div className="grid grid-cols-7 shrink-0 border-b border-gray-200">
                    {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((d, i) => (
                        <div key={d} className={`text-center py-2 text-xs md:text-sm font-bold uppercase tracking-wider ${i > 4 ? 'text-gray-400' : 'text-gray-500'}`}>{d}</div>
                    ))}
                </div>
                
                {/* Сетка дней - растягивается на 100% высоты и ширины */}
                <div className="grid grid-cols-7 grid-rows-6 flex-1 min-h-0 bg-white">
                    {cells.map(cell => (
                        <div key={cell.key} className="border-r border-b border-gray-200 p-1 flex flex-col hover:bg-gray-50 min-h-0 overflow-hidden">
                            {!cell.empty && (
                                <>
                                    <div className="text-center mb-1 shrink-0">
                                        <span className={`inline-flex items-center justify-center text-sm font-medium w-6 h-6 md:w-7 md:h-7 rounded-full ${cell.isToday ? 'bg-blue-600 text-white' : 'text-gray-700'}`}>{cell.day}</span>
                                    </div>
                                    <div className="flex flex-col gap-1 overflow-y-auto custom-scrollbar min-h-0 flex-1">
                                        {cell.events.map(e => (
                                            <div key={e.id} onClick={() => onEventClick(e)} className={`text-xs truncate px-1.5 py-1 rounded cursor-pointer leading-tight shadow-sm hover:opacity-90 ${getEventColors(e)}`} title={e.title}>
                                                {formatTime(e.start_time)} {e.title}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // --- РЕНДЕР ГОДА (ПОДНЯТ НАВЕРХ НА 15%) ---
    const renderYearView = () => {
        const year = currentDate.getFullYear();
        const months = Array.from({length: 12}).map((_, m) => {
            const daysInMonth = new Date(year, m + 1, 0).getDate();
            const start = new Date(year, m, 1).getDay();
            const startDay = start === 0 ? 6 : start - 1;
            return { month: m, days: daysInMonth, startDay };
        });
        const monthNames = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];
        const todayStr = getLocalDateStr(new Date());

        return (
            // Контейнер 100% высоты, выравнивание по ВЕРХНЕМУ краю (items-start) и сдвиг вниз (pt-4)
            <div className="flex items-start justify-center w-full h-full bg-white overflow-hidden p-2 md:pt-6">
                {/* Высота 85% подтягивает ряды друг к другу, оставляя 15% внизу пустыми */}
                <div className="w-full max-w-5xl h-[85%] grid grid-cols-3 xl:grid-cols-4 grid-rows-4 xl:grid-rows-3 gap-2 md:gap-4 min-h-0">
                    {months.map(m => (
                        <div key={m.month} className="flex flex-col min-h-0 items-center justify-center">
                            <h3 className="font-bold text-gray-800 text-sm md:text-base mb-2">{monthNames[m.month]}</h3>
                            <div className="grid grid-cols-7 gap-x-1.5 gap-y-1 text-center text-xs w-full max-w-[180px]">
                                {['П', 'В', 'С', 'Ч', 'П', 'С', 'В'].map((d, i) => <div key={i} className="text-gray-400 font-medium mb-1">{d}</div>)}
                                {Array(m.startDay).fill(null).map((_, i) => <div key={`e-${i}`}></div>)}
                                {Array.from({length: m.days}).map((_, i) => {
                                    const dateStr = `${year}-${String(m.month + 1).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`;
                                    const hasEvents = filteredEvents.some(e => getLocalDateStr(e.start_time) === dateStr);
                                    const isToday = dateStr === todayStr;
                                    return (
                                        <div key={i} className="flex items-center justify-center aspect-square">
                                            <div className={`w-5 h-5 md:w-6 md:h-6 flex items-center justify-center rounded-full ${isToday ? 'bg-blue-600 text-white' : hasEvents ? 'bg-blue-100 text-blue-800 font-bold cursor-pointer hover:bg-blue-200' : 'text-gray-700'}`}>
                                                {i + 1}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // --- РЕНДЕР РАСПИСАНИЯ ---
    const renderScheduleView = () => {
        const year = miniCalendarDate.getFullYear();
        const month = miniCalendarDate.getMonth();
        
        const scheduleEvents = filteredEvents.filter(e => {
            const d = new Date(e.start_time);
            return d.getFullYear() === year && d.getMonth() === month;
        }).sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

        return (
            <div className="h-full overflow-y-auto p-4 md:p-8 w-full max-w-4xl mx-auto bg-white">
                <h2 className="text-3xl md:text-4xl font-normal text-gray-800 mb-8 capitalize">{miniCalendarDate.toLocaleString('ru-RU', { month: 'long', year: 'numeric' })}</h2>
                {scheduleEvents.length === 0 ? (
                    <div className="text-lg text-gray-500 text-center mt-20">Нет запланированных событий на этот месяц</div>
                ) : (
                    <div className="flex flex-col gap-5">
                        {scheduleEvents.map(e => {
                            const d = new Date(e.start_time);
                            return (
                                <div key={e.id} onClick={() => onEventClick(e)} className="flex gap-4 md:gap-6 cursor-pointer group">
                                    <div className="w-16 shrink-0 text-center mt-1">
                                        <div className="text-sm font-bold text-gray-500 uppercase">{d.toLocaleDateString('ru-RU', { weekday: 'short' })}</div>
                                        <div className="text-3xl md:text-4xl font-normal text-gray-800">{d.getDate()}</div>
                                    </div>
                                    <div className={`flex-1 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden text-white ${getEventColors(e).split(' ')[0]}`}>
                                        <div className="text-base font-bold mb-1 leading-tight">{e.title}</div>
                                        <div className="text-sm opacity-90">{formatTime(e.start_time)} • {e.target_group_name || 'Личное'}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    if (loading) return <div className="flex-1 flex items-center justify-center w-full h-full bg-white"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;

    return (
        // flex-1 и overflow-hidden не дают странице уехать за края экрана
        <div className="flex-1 w-full h-full overflow-hidden relative bg-white flex">
            {currentView === 'month' && renderMonthView()}
            {currentView === 'schedule' && renderScheduleView()}
            {currentView === 'day' && renderDayView()}
            {currentView === 'week' && renderWeekView()}
            {currentView === 'year' && renderYearView()}

            <button className="absolute bottom-6 right-6 md:bottom-8 md:right-8 w-14 h-14 bg-white border border-gray-100 rounded-full flex items-center justify-center text-blue-600 hover:bg-gray-50 transition-colors z-20 shadow-lg hover:scale-105" title="Добавить событие">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            </button>
        </div>
    );
}